"use client";

import { useEffect, useRef, useState } from "react";
import { Camera, Microscope, Stethoscope } from "lucide-react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  num: "01",
  title: "Visual Review",
  desc: "We look for visible signs of moisture, staining, organic growth, water damage, and conditions that may contribute to mold concerns.",
  icon: Camera
}, {
  num: "02",
  title: "Air or Surface Sampling",
  desc: "When requested, we collect air samples and/or surface samples and send them to a qualified laboratory for analysis.",
  icon: Microscope
}, {
  num: "03",
  title: "Lab Results & Clear Explanation",
  desc: "Once results are available, we explain the findings in plain language and recommend next steps when elevated conditions are present.",
  icon: Stethoscope
}];
export default function ThreeStepProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="248656" id="overview" className="bg-background text-foreground py-20 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8">
          {steps.map((step, i) => <div key={i} data-index={i} className={cn("relative flex flex-col items-center text-center transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[20px]")} style={{
          transitionDelay: `${i * 200}ms`
        }}>
              {}
              <div className="absolute -top-10 left-1/2 -translate-x-1/2 text-[140px] md:text-[160px] font-mono font-bold text-primary/10 leading-none z-0 select-none pointer-events-none">
                {step.num}
              </div>

              {}
              <div className="relative z-10 flex flex-col items-center mt-16 md:mt-20">
                <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-lg bg-card shadow-md border border-border">
                  <step.icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-default text-2xl font-semibold text-foreground mb-4 leading-tight">
                  {step.title}
                </h3>
                <p className="font-default text-base leading-[1.6] text-muted-foreground max-w-[280px] mx-auto">
                  {step.desc}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}